import { Node } from 'slate';
import { formatTime } from './timeFormatter';

/**
 * Slate Word Plugin
 * 
 * Converts block text and words array into Slate nodes with word-level structure
 * Each word is a separate inline element (type: 'word') with metadata preserved
 */

/**
 * Convert block data to Slate value
 * Creates a paragraph node containing word inline elements
 * Each word is an inline element (type: 'word') not a text node
 */
export const blockToSlateValue = (block) => {
  return blockToSlateValueWithOptions(block);
};

/**
 * Convert block data to Slate value with options.
 * Use `wordLevel: false` to render plain text (no per-word spans).
 */
export const blockToSlateValueWithOptions = (block, options = {}) => {
  const { wordLevel = true } = options;
  if (!block) {
    return [
      {
        type: 'paragraph',
        children: [{ text: '' }],
      },
    ];
  }

  // If we have word-level data, use it
  if (wordLevel && block.words && block.words.length > 0) {
    const children = [];

    block.words.forEach((word, idx) => {
      const wordText = word.word || word.raw || '';
      
      // Create INLINE ELEMENT for word (not text node!)
      children.push({
        type: 'word',  // <-- This is the key!
        wordData: {
          index: word.index !== undefined ? word.index : idx,
          start: word.start,
          end: word.end,
          raw: word.raw || wordText,
          speaker: word.speaker,
          probability: word.probability,
        },
        children: [{ text: wordText }],  // Text is child of word element
      });

      // Add space between words (except after last word)
      if (idx < block.words.length - 1) {
        children.push({ text: ' ' });
      }
    });

    return [
      {
        type: 'paragraph',
        blockId: block.id,
        children: children.length > 0 ? children : [{ text: '' }],
      },
    ];
  }

  // Fallback: no word-level data, just use block.text
  return [
    {
      type: 'paragraph',
      blockId: block.id,
      children: [{ text: block.text || '' }],
    },
  ];
};

/**
 * Convert Slate value back to plain text
 * Handles inline word elements
 */
export const slateValueToText = (value) => {
  if (!value || value.length === 0) return '';
  
  return value
    .map((node) => {
      if (node.type === 'paragraph' && node.children) {
        return node.children
          .map((child) => {
            if (child.type === 'word') {
              return Node.string(child);
            }
            return child.text || '';
          })
          .join('');
      }
      return Node.string(node);
    })
    .join('\n');
};

/**
 * Extract words array from Slate value
 * Tries to preserve word metadata where possible
 * Works with inline word elements
 */
export const slateValueToWords = (value, originalWords = []) => {
  if (!value || value.length === 0) return [];

  const words = [];
  
  for (const node of value) {
    if (node.children) {
      for (const child of node.children) {
        // Word element (inline element with type: 'word')
        if (child.type === 'word' && child.wordData) {
          const wordText = Node.string(child).trim();
          if (wordText) {
            words.push({
              index: child.wordData.index,
              word: wordText,
              raw: child.wordData.raw,
              start: child.wordData.start,
              end: child.wordData.end,
              speaker: child.wordData.speaker,
              probability: child.wordData.probability,
            });
          }
        }
        // Text node (new text added by user, not a word element)
        else if (child.text && !child.type) {
          const wordText = child.text.trim();
          if (wordText) {
            // Try to split into individual words if it's a phrase
            const individualWords = wordText.split(/\s+/);
            individualWords.forEach((w) => {
              if (w) {
                words.push({
                  word: w,
                  raw: w,
                  // Timestamps will be interpolated by wordTimestampSync
                  start: null,
                  end: null,
                  speaker: null,
                  probability: null,
                });
              }
            });
          }
        }
      }
    }
  }

  return words;
};

/**
 * Slate plugin that marks 'word' as inline element
 */
export const withWords = (editor) => {
  const { isInline, isVoid } = editor;

  // Words are inline elements (not block-level)
  editor.isInline = (element) => {
    return element.type === 'word' ? true : isInline(element);
  };

  // Words are not void (they contain text)
  editor.isVoid = (element) => {
    return element.type === 'word' ? false : isVoid(element);
  };

  return editor;
};

/**
 * Get confidence class based on probability
 */
const getConfidenceClass = (probability) => {
  if (!probability) return 'medium-confident';
  if (probability >= 0.9) return 'very-confident';
  if (probability >= 0.7) return 'confident';
  if (probability >= 0.5) return 'medium-confident';
  return 'low-confident';
};

/**
 * Custom renderElement function for Slate editor
 * Renders word elements as spans with data attributes
 */
export const renderElement = (props, { isPlaying, currentTime, onWordClick }) => {
  const { attributes, children, element } = props;

  // Paragraph element
  if (element.type === 'paragraph') {
    return <div {...attributes} style={{ display: 'contents' }}>{children}</div>;
  }

  // Word element - each word is a separate span!
  if (element.type === 'word') {
    const wordData = element.wordData;
    const isActive =
      isPlaying === true &&
      typeof currentTime === 'number' &&
      Number.isFinite(currentTime) &&
      typeof wordData?.start === 'number' &&
      typeof wordData?.end === 'number' &&
      Number.isFinite(wordData.start) &&
      Number.isFinite(wordData.end) &&
      currentTime >= wordData.start &&
      currentTime < wordData.end;

    const confidenceClass = getConfidenceClass(wordData.probability);

    return (
      <span
        {...attributes}
        data-word-index={wordData.index}
        data-from={wordData.start}
        data-to={wordData.end}
        data-tsf={formatTime(wordData.start)}
        data-tst={formatTime(wordData.end)}
        className={`word ${confidenceClass} ${isActive ? 'word-active' : ''}`}
        onDoubleClick={(e) => {
          if (!onWordClick) return;
          
          const selection = window.getSelection();
          const hasSelection = selection && selection.toString().length > 0;
          
          // Ako postoji selekcija, proveri da li je unutar kliknutog elementa
          if (hasSelection) {
            const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
            if (range) {
              const clickedElement = e.currentTarget;
              // Proveri da li je selekcija unutar kliknutog elementa
              const ancestor = range.commonAncestorContainer;
              const selectionWithinElement = 
                clickedElement.contains(ancestor) ||
                (ancestor.nodeType === 3 && // TEXT_NODE
                 clickedElement.contains(ancestor.parentNode));
              
              // Ako je selekcija unutar elementa (samo ta reč), pozovi onWordClick
              if (selectionWithinElement) {
                e.stopPropagation();
                e.preventDefault();
                onWordClick(wordData);
                // Očisti selekciju nakon poziva
                selection.removeAllRanges();
                return;
              }
            }
            // Ako je selekcija van elementa (korisnik je selektovao više reči), ne pozivaj onWordClick
            return;
          }
          
          // Ako nema selekcije, pozovi onWordClick
          e.stopPropagation();
          e.preventDefault();
          onWordClick(wordData);
        }}
      >
        {children}
      </span>
    );
  }

  // Fallback
  return <span {...attributes}>{children}</span>;
};

/**
 * Custom renderLeaf function for Slate editor
 * Renders regular text nodes (spaces, etc.)
 */
export const renderLeaf = (props) => {
  const { attributes, children } = props;
  return <span {...attributes}>{children}</span>;
};

